'use client';

import { useRef, type PointerEvent } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface WalletSheetResizeControlProps {
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
}

export function WalletSheetResizeControl({ expanded, onExpandedChange }: WalletSheetResizeControlProps) {
  const gesture = useRef<{ id: number; y: number } | null>(null);
  const suppressClick = useRef(false);
  const Icon = expanded ? ChevronDown : ChevronUp;

  function startGesture(event: PointerEvent<HTMLButtonElement>) {
    suppressClick.current = false;
    if (!event.isPrimary || event.button !== 0) return;
    gesture.current = { id: event.pointerId, y: event.clientY };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function finishGesture(event: PointerEvent<HTMLButtonElement>) {
    const start = gesture.current;
    if (!start || start.id !== event.pointerId) return;
    gesture.current = null;
    const distance = start.y - event.clientY;
    if (Math.abs(distance) < 30) return;
    suppressClick.current = true;
    onExpandedChange(distance > 0);
  }

  return (
    <button
      type="button"
      data-testid="wallet-sheet-resize"
      aria-label={expanded ? 'Collapse Shopping Wallet' : 'Expand Shopping Wallet'}
      aria-expanded={expanded}
      className="mx-auto flex min-h-11 touch-none items-center justify-center gap-1 rounded-md px-5 text-xs font-medium text-[#666666] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-motitoBlue"
      onPointerDown={startGesture}
      onPointerUp={finishGesture}
      onPointerCancel={() => { gesture.current = null; suppressClick.current = false; }}
      onClick={(event) => {
        if (event.detail === 0 || !suppressClick.current) onExpandedChange(!expanded);
        suppressClick.current = false;
      }}
    >
      <Icon aria-hidden="true" className="size-4" />
      {expanded ? 'Collapse' : 'Expand'}
    </button>
  );
}
